import { useState, useEffect } from 'react'

export type BreakpointKey = 'sm' | 'md' | 'lg' | 'xl' | '2xl'
export type DeviceType = 'mobile' | 'tablet' | 'desktop'

export interface ResponsiveState {
  isMobile: boolean
  isTablet: boolean
  isDesktop: boolean
  deviceType: DeviceType
  width: number
  height: number
  // Breakpoint checks
  screens: Record<BreakpointKey, boolean>
}

const breakpoints: Record<BreakpointKey, number> = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  '2xl': 1536,
}

/**
 * 响应式Hook - 检测设备类型和屏幕尺寸
 * @returns ResponsiveState对象，包含设备类型、断点状态等
 */
export const useResponsive = (): ResponsiveState => {
  const [state, setState] = useState<ResponsiveState>(() => {
    const width = window.innerWidth
    const height = window.innerHeight

    return {
      isMobile: width < breakpoints.md,
      isTablet: width >= breakpoints.md && width < breakpoints.lg,
      isDesktop: width >= breakpoints.lg,
      deviceType: width < breakpoints.md ? 'mobile' : width < breakpoints.lg ? 'tablet' : 'desktop',
      width,
      height,
      screens: {
        sm: width >= breakpoints.sm,
        md: width >= breakpoints.md,
        lg: width >= breakpoints.lg,
        xl: width >= breakpoints.xl,
        '2xl': width >= breakpoints['2xl'],
      },
    }
  })

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth
      const height = window.innerHeight

      setState({
        isMobile: width < breakpoints.md,
        isTablet: width >= breakpoints.md && width < breakpoints.lg,
        isDesktop: width >= breakpoints.lg,
        deviceType: width < breakpoints.md ? 'mobile' : width < breakpoints.lg ? 'tablet' : 'desktop',
        width,
        height,
        screens: {
          sm: width >= breakpoints.sm,
          md: width >= breakpoints.md,
          lg: width >= breakpoints.lg,
          xl: width >= breakpoints.xl,
          '2xl': width >= breakpoints['2xl'],
        },
      })
    }

    // 使用防抖优化性能
    let timeoutId: NodeJS.Timeout
    const debouncedHandleResize = () => {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(handleResize, 150)
    }

    window.addEventListener('resize', debouncedHandleResize)

    return () => {
      window.removeEventListener('resize', debouncedHandleResize)
      clearTimeout(timeoutId)
    }
  }, [])

  return state
}
